/**
 * Claims Ratio Analysis Dashboard
 * Tracks claims frequency, loss ratio, and profitability by plan/company
 * Helps identify at-risk products and pricing adjustments
 */

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { TrendingDown, FileText, DollarSign, BarChart3 } from 'lucide-react';

export default function ClaimsRatioAnalysis() {
  const [dateRange, setDateRange] = useState<'month' | 'quarter' | 'year'>('quarter');
  const [lossRatioByPlan, setLossRatioByPlan] = useState<any[]>([]);
  const [claimsTrend, setClaimsTrend] = useState<any[]>([]);
  const [topClaimTypes, setTopClaimTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    await Promise.all([
      loadLossRatio(),
      loadClaimsTrend(),
      loadTopClaimTypes()
    ]);
    setLoading(false);
  };

  const loadLossRatio = async () => {
    // Get premium and claims by plan
    const { data: agreements } = await supabase
      .from('warranty_agreements')
      .select('plan_name, annual_price_cents')
      .eq('status', 'active');

    const { data: claims } = await supabase
      .from('warranty_claims')
      .select('agreement_id, actual_cost_cents, warranty_agreements(plan_name)')
      .eq('status', 'paid');

    if (agreements && claims) {
      const premiumByPlan: any = {};
      const claimsByPlan: any = {};

      agreements.forEach((a: any) => {
        const plan = a.plan_name || 'Unknown';
        premiumByPlan[plan] = (premiumByPlan[plan] || 0) + (a.annual_price_cents || 0);
      });

      claims.forEach((c: any) => {
        const plan = c.warranty_agreements?.plan_name || 'Unknown';
        claimsByPlan[plan] = (claimsByPlan[plan] || 0) + (c.actual_cost_cents || 0);
      });

      const ratios = Object.keys(premiumByPlan).map(plan => ({
        plan,
        premium: premiumByPlan[plan] / 100,
        claims: (claimsByPlan[plan] || 0) / 100,
        lossRatio: premiumByPlan[plan] > 0 
          ? ((claimsByPlan[plan] || 0) / premiumByPlan[plan] * 100).toFixed(1)
          : '0.0'
      }));

      setLossRatioByPlan(ratios);
    }
  };

  const loadClaimsTrend = async () => {
    // Simulated monthly claims vs premium trend
    setClaimsTrend([
      { month: 'Jan', premium: 100000, claims: 45000, ratio: 45 },
      { month: 'Feb', premium: 105000, claims: 48000, ratio: 45.7 },
      { month: 'Mar', premium: 110000, claims: 52000, ratio: 47.3 },
      { month: 'Apr', premium: 115000, claims: 55000, ratio: 47.8 },
      { month: 'May', premium: 120000, claims: 58000, ratio: 48.3 },
      { month: 'Jun', premium: 125000, claims: 60000, ratio: 48 }
    ]);
  };

  const loadTopClaimTypes = async () => {
    const { data } = await supabase
      .from('warranty_claims')
      .select('claim_type, actual_cost_cents')
      .eq('status', 'paid');

    if (data) {
      const grouped = data.reduce((acc: any, row: any) => {
        const type = row.claim_type || 'Other';
        acc[type] = (acc[type] || 0) + (row.actual_cost_cents || 0);
        return acc;
      }, {});

      const sorted = Object.entries(grouped)
        .map(([type, cost]) => ({
          type,
          cost: (cost as number) / 100,
          count: data.filter((c: any) => (c.claim_type || 'Other') === type).length
        }))
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 10);

      setTopClaimTypes(sorted);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-4xl mb-4">📊</div>
          <div className="text-gray-600">Loading claims analytics...</div>
        </div>
      </div>
    );
  }

  const avgLossRatio = lossRatioByPlan.length > 0
    ? (lossRatioByPlan.reduce((sum, item) => sum + parseFloat(item.lossRatio), 0) / lossRatioByPlan.length).toFixed(1)
    : '0.0';

  const isHealthy = parseFloat(avgLossRatio) <= 60;

  return (
    <AdminPageLayout
      title="Claims Ratio Analysis"
      subtitle="Loss ratios, trends, and profitability insights"
      actions={
        <>
          {(['month', 'quarter', 'year'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-lg font-medium ${
                dateRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {range === 'month' ? 'Month' : range === 'quarter' ? 'Quarter' : 'Year'}
            </button>
          ))}
        </>
      }
    >
      {/* Summary Cards */}
      <StatsGrid cols={4}>
        <StatCard
          label="Avg Loss Ratio"
          value={`${avgLossRatio}%`}
          icon={TrendingDown}
          variant={isHealthy ? 'success' : 'critical'}
          subtitle={isHealthy ? '✓ Healthy' : '⚠️ Above target'}
        />
        <StatCard
          label="Total Claims"
          value={topClaimTypes.reduce((sum, item) => sum + item.count, 0)}
          icon={FileText}
          subtitle="This period"
        />
        <StatCard
          label="Claims Paid"
          value={`$${topClaimTypes.reduce((sum, item) => sum + item.cost, 0).toLocaleString()}`}
          icon={DollarSign}
          subtitle="Total payouts"
        />
        <StatCard
          label="Avg Claim Size"
          value={`$${topClaimTypes.length > 0
            ? (topClaimTypes.reduce((sum, item) => sum + item.cost, 0) / topClaimTypes.reduce((sum, item) => sum + item.count, 0)).toFixed(0)
            : '0'}`}
          icon={BarChart3}
          subtitle="Per claim"
        />
      </StatsGrid>

      {/* Loss Ratio Trend */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Loss Ratio Trend</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={claimsTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="premium" stroke="#10B981" strokeWidth={2} name="Premium" />
            <Line yAxisId="left" type="monotone" dataKey="claims" stroke="#EF4444" strokeWidth={2} name="Claims" />
            <Line yAxisId="right" type="monotone" dataKey="ratio" stroke="#3B82F6" strokeWidth={2} name="Loss Ratio %" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Loss Ratio by Plan */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Loss Ratio by Plan</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={lossRatioByPlan}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="plan" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="premium" fill="#10B981" name="Premium" />
            <Bar dataKey="claims" fill="#EF4444" name="Claims" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Top Claim Types */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Top Claim Types by Cost</h2>
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claim Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topClaimTypes.map((claim) => (
                <tr key={claim.type}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {claim.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {claim.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${claim.cost.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${(claim.cost / claim.count).toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminPageLayout>
  );
}
